import { useState, useEffect, useRef, useContext } from 'react'
import Joyride, { ACTIONS, EVENTS, CallBackProps, Step } from 'react-joyride'
import { CustomTooltips } from './customTooltips'
import { useProject } from '../project/hook'
import './tutorial.css'
import { SetupSlideModal } from '../modals/help/setupSlideModal'
import { useTutorialContext } from './useTutorialContext'
import { usePreviewVisible } from '../preview/usePreviewVisible'
import { OpeningVideo } from './openingVideo'
import { UserContext } from '@/components/user/provider'

interface TutorialProps {
  tutorialEndCallback: () => void
}
const steps: Step[] = [
  {
    target: '.blocklyFlyoutBackground',
    content: '',
    disableBeacon: true,
    placement: 'right',
    spotlightClicks: false,
    data: {
      content1: (
        <>
          ここに
          <ruby>
            入<rt>はい</rt>
          </ruby>
          っているパーツを
          <ruby>
            使<rt>つか</rt>
          </ruby>
          って、
        </>
      ),
      content2: <>プログラミングをしていくよ！</>,
      buttonLabel: 'つぎへ',
    },
  },
  {
    target: '#resize-handler',
    content: '',
    disableBeacon: true,
    placement: 'right',
    spotlightClicks: false,
    data: {
      content1: (
        <>
          <ruby>
            動画<rt>どうが</rt>
          </ruby>
          を
          <ruby>
            大<rt>おお</rt>
          </ruby>
          きくしたいときは、
        </>
      ),
      content2: (
        <>
          ここをつかんで
          <ruby>
            右<rt>みぎ</rt>
          </ruby>
          にひっぱろう！
        </>
      ),
      buttonLabel: 'つぎへ',
    },
  },
  {
    target: '.window-video-drag-handle',
    content: '',
    disableBeacon: true,
    placement: 'top',
    spotlightClicks: false,
    data: {
      content1: (
        <>
          <ruby>
            動画<rt>どうが</rt>
          </ruby>
          の
          <ruby>
            場所<rt>ばしょ</rt>
          </ruby>
          は、このエリアをつかんで
          <ruby>
            動<rt>うご</rt>
          </ruby>
          かせるよ！
        </>
      ),
      buttonLabel: 'つぎへ',
    },
  },
  {
    target: '.window-video-frame',
    content: '',
    disableBeacon: true,
    placement: 'right',
    spotlightClicks: true,
    data: {
      content1: (
        <>
          さっそく
          <ruby>
            動画<rt>どうが</rt>
          </ruby>
          をスタートしてみよう！
        </>
      ),
      content2: <>レッツ・プログラミング！</>,
      buttonLabel: 'スタート',
      content1Style: 'tooltipText1NoAnim',
      content2Style: 'tooltipText2Emphasis',
    },
  },
]

export const Tutorial = ({ tutorialEndCallback }: TutorialProps) => {
  const { loading, canEdit, missionInfo } = useProject()
  const { isOnlineUser } = useContext(UserContext)

  const { startInitialTutorial, markOpeningVideoClosed } = useTutorialContext()
  // プレビュー表示時は Studio のセットアップが不要なため、オープニング動画後の
  // セットアップスライドをスキップしてそのままチュートリアルを開始する。
  const previewVisible = usePreviewVisible()

  const [spotlightClicks, setSpotlightClicks] = useState<boolean>(false)
  const [stepIndex, setStepIndex] = useState<number>(0)
  const [blocklyLoaded, setBlocklyLoaded] = useState<boolean>(false)
  const [showOpeningVideoModal, setShowOpeningVideoModal] =
    useState<boolean>(startInitialTutorial)
  const [showSetupModal, setShowSetupModal] = useState<boolean>(false)
  const [isOpeningVideoEnded, setIsOpeningVideoEnded] = useState<boolean>(false)
  // Widget を読み込めなかった、または一定時間再生が始まらなかった。
  // 利用者がモーダルで止まらないよう、先へ進むボタンを出す
  const [openingVideoStalled, setOpeningVideoStalled] = useState<boolean>(false)

  const timer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const checkBlocklyLoaded = () => {
      const blocklyElement = document.querySelector('.blocklyFlyoutBackground')
      if (blocklyElement) {
        setBlocklyLoaded(true)
      } else {
        setTimeout(checkBlocklyLoaded, 500)
      }
    }
    checkBlocklyLoaded()
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  useEffect(() => {
    setShowOpeningVideoModal(startInitialTutorial)
    // オープニングを出し直すときは、前回の終了・停止の状態を持ち越さない。
    // TutorialProvider はプロジェクトを読み直すたびに startInitialTutorial を
    // false -> true に戻すが、Tutorial はアンマウントされず、どちらも一度 true に
    // なると戻らないため、新しいオープニングで最初からボタンが出てしまう。
    // Joyride の現在のステップ位置、スポットライト越しのクリックの許可、その
    // 許可を出す予約済みのタイマーも戻す。戻さないと、出し直したガイドが前回の
    // 終了時に残った範囲外のステップ位置を受け取り、ステップを表示せず、終了も
    // 通知しない
    if (startInitialTutorial) {
      setIsOpeningVideoEnded(false)
      setOpeningVideoStalled(false)
      setStepIndex(0)
      setSpotlightClicks(false)
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
    }
  }, [startInitialTutorial])

  const handleTutorialCallback = (data: CallBackProps) => {
    const { action, index, type } = data
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const currentIndex = index + (action === ACTIONS.PREV ? -1 : 1)
      setStepIndex(currentIndex)
      const stepsCount = steps.length

      if (currentIndex + 1 === stepsCount) {
        timer.current = setTimeout(() => {
          setSpotlightClicks(true)
        }, 6000)
      } else if (currentIndex === stepsCount) {
        tutorialEndCallback()
      }
    }
  }

  if (!canEdit) {
    return null
  }

  if (showOpeningVideoModal && !loading) {
    return (
      <div
        className="opening-video-modal"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="modal-backdrop"></div>
        <div className="window-video-modal">
          {missionInfo && (
            <OpeningVideo
              missionId={missionInfo.id}
              isOnlineUser={isOnlineUser}
              onEnded={() => setIsOpeningVideoEnded(true)}
              // embed.js を読み込めない（環境変数の未設定・配信元に届かない等）
              // ときも、モーダルで利用者が詰まらないようボタンを出して先へ進める
              onLoadError={() => setOpeningVideoStalled(true)}
              // Widget の中の再生エラー（未ログイン、CORS、動画の未配置等）は親へ
              // 届かないため、一定時間再生が始まらないときも同様にボタンを出す
              onStartTimeout={() => setOpeningVideoStalled(true)}
            />
          )}
          {(isOpeningVideoEnded || openingVideoStalled) && (
            <button
              className="modal-close-button"
              onClick={() => {
                setShowOpeningVideoModal(false)
                // 閉じたことを動画窓へ知らせる（Widget の初期化を始めてよい）
                if (missionInfo) {
                  markOpeningVideoClosed(missionInfo.id)
                }
                if (!previewVisible) {
                  setShowSetupModal(true)
                }
              }}
            >
              {previewVisible ? 'さっそくはじめる！' : 'セットアップをはじめる'}
            </button>
          )}
        </div>
      </div>
    )
  } else {
    return (
      <>
        <SetupSlideModal
          isOpen={showSetupModal}
          onClose={() => {
            setShowSetupModal(false)
          }}
        />
        <Joyride
          callback={handleTutorialCallback}
          steps={steps}
          stepIndex={stepIndex}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tooltipComponent={(props: any) => (
            <CustomTooltips {...props} spotlightClicks={spotlightClicks} />
          )}
          spotlightClicks={spotlightClicks}
          continuous
          run={
            blocklyLoaded &&
            startInitialTutorial &&
            !showOpeningVideoModal &&
            !showSetupModal
          }
          disableOverlayClose
          disableCloseOnEsc
          hideBackButton
          hideCloseButton
          disableScrollParentFix
          disableScrolling
          scrollToFirstStep={false}
          styles={{
            options: {
              arrowColor: 'transparent',
              zIndex: 999999,
            },
            overlay: { height: '100vh', width: '100vw' },
            spotlight: {
              borderRadius: '8px',
            },
          }}
          floaterProps={{
            disableAnimation: true,
          }}
        />
      </>
    )
  }
}
